import { createClient, type SanityClient } from '@sanity/client'
import {
  createImageUrlBuilder,
  type ImageUrlBuilder,
  type SanityImageSource,
} from '@sanity/image-url'
import {
  AUTHOR_PROJECTION,
  COAUTHOR_PROJECTION,
  autoRelatedQuery,
  categoryFallbackQuery,
  postWithRelatedQuery,
} from './blogQueries'
import { SITE_NAME, type PageMeta } from './seo'
import {
  buildImageSrcSet,
  responsiveImageWidths,
} from './imageDimensions'

let client: SanityClient | null = null

function getClient(): SanityClient {
  if (!client) {
    const projectId = import.meta.env.VITE_SANITY_PROJECT_ID
    if (!projectId) {
      throw new Error('Missing VITE_SANITY_PROJECT_ID - add it to .env (see .env.example)')
    }
    client = createClient({
      projectId,
      dataset: import.meta.env.VITE_SANITY_DATASET ?? 'production',
      apiVersion: '2024-01-01',
      useCdn: true,
    })
  }
  return client
}

export function urlFor(source: SanityImageSource) {
  return createImageUrlBuilder(getClient()).image(source)
}

function hasImageAsset(source: unknown): source is { asset: { _ref?: string; _id?: string; url?: string } } {
  const asset = (source as { asset?: unknown })?.asset
  if (!asset || typeof asset !== 'object') return false
  return (
    Boolean('_ref' in asset && asset._ref) ||
    Boolean('_id' in asset && asset._id) ||
    Boolean('url' in asset && asset.url)
  )
}

export function safeImageUrl(
  source: unknown,
  transform: (builder: ImageUrlBuilder) => ImageUrlBuilder,
): string | null {
  if (!hasImageAsset(source)) return null
  return transform(urlFor(source as SanityImageSource)).url()
}

/** Build a width-based srcset using the same Sanity image builder as `src`. */
export function safeImageSrcSet(
  source: unknown,
  maxWidth: number,
  transform: (builder: ImageUrlBuilder, width: number) => ImageUrlBuilder,
  candidates?: readonly number[],
): string | null {
  if (!hasImageAsset(source)) return null
  const widths = responsiveImageWidths(source, maxWidth, candidates)
  return buildImageSrcSet(widths, (width) =>
    transform(urlFor(source as SanityImageSource), width).url(),
  )
}

export type Author = {
  name: string
  slug: { current: string }
  role?: string
  bio?: string
  twitter?: string
  linkedin?: string
  github?: string
  website?: string
  email?: string
  photo?: { asset: unknown; hotspot?: unknown; width?: number; height?: number } | null
}

export type PostSummary = {
  _id: string
  title: string
  slug: { current: string }
  publishedAt: string
  _updatedAt?: string
  author: Author | null
  coAuthors?: (Author | null)[]
  category: string
  tags?: string[]
  excerpt: string
  coverImage: { asset: unknown; alt: string; hotspot?: unknown; width?: number; height?: number } | null
}

export type PostFull = PostSummary & {
  body: unknown[]
  relatedPosts?: PostSummary[]
  seo?: {
    metaTitle?: string
    metaDescription?: string
    ogImage?: { asset: unknown; alt?: string; width?: number; height?: number } | null
  }
}

const POST_FILTER = `_type == "post" && !(_id in path("drafts.**")) && publishedAt <= now()`

const POST_SHARE_PROJECTION = `{
  title,
  excerpt,
  coverImage { asset, alt, hotspot, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height },
  seo {
    metaTitle,
    metaDescription,
    ogImage { asset, alt, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height }
  }
}`

export async function getAllPosts(): Promise<PostSummary[]> {
  return getClient().fetch(
    `*[${POST_FILTER}] | order(publishedAt desc) {
      _id,
      title,
      slug,
      publishedAt,
      "author": author-> ${AUTHOR_PROJECTION},
      "coAuthors": coAuthors[]-> ${COAUTHOR_PROJECTION},
      category,
      excerpt,
      coverImage { asset, alt, hotspot, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height }
    }`,
  )
}

export async function getAllPostSlugs(): Promise<string[]> {
  const rows: { slug: string }[] = await getClient().fetch(
    `*[${POST_FILTER}]{ "slug": slug.current }`,
  )
  return rows.map((row) => row.slug).filter(Boolean)
}

export async function getPostBySlug(slug: string): Promise<PostFull | null> {
  return getClient().fetch(postWithRelatedQuery, { slug })
}

function validPostSummary(post: PostSummary | null | undefined): post is PostSummary {
  return post != null && typeof post._id === 'string'
}

export async function getRelatedPosts(post: PostFull): Promise<PostSummary[]> {
  const manualRelated = post.relatedPosts?.filter(validPostSummary)
  if (manualRelated?.length) {
    return manualRelated
  }

  if (post.tags?.length) {
    const byTags = await getClient().fetch<PostSummary[]>(autoRelatedQuery, {
      slug: post.slug.current,
      tags: post.tags,
    })
    if (byTags.length > 0) return byTags
  }

  if (post.category) {
    const byCategory = await getClient().fetch<PostSummary[]>(categoryFallbackQuery, {
      slug: post.slug.current,
      category: post.category,
    })
    if (byCategory.length > 0) return byCategory
  }

  return []
}

function ogImageUrl(source: unknown): string | null {
  return safeImageUrl(source, (b) => b.width(1200).height(630).format('jpg').quality(80))
}

export function resolvePostSeoImage(post: {
  coverImage: PostSummary['coverImage']
  seo?: PostFull['seo']
}): string | undefined {
  return ogImageUrl(post.seo?.ogImage) ?? ogImageUrl(post.coverImage) ?? undefined
}

export async function getPostMetaForShare(
  slug: string,
): Promise<(PageMeta & { ogImage?: string }) | null> {
  const post = await getClient().fetch<{
    title: string
    excerpt: string
    coverImage: PostSummary['coverImage']
    seo?: PostFull['seo']
  } | null>(`*[${POST_FILTER} && slug.current == $slug][0] ${POST_SHARE_PROJECTION}`, { slug })

  if (!post) return null

  const seoTitle = post.seo?.metaTitle ?? post.title

  return {
    title: `${seoTitle} | ${SITE_NAME} Blog`,
    description: post.seo?.metaDescription ?? post.excerpt,
    path: `/blog/${slug}`,
    ogImage: resolvePostSeoImage(post),
  }
}
